import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import FolderIcon from "@mui/icons-material/Folder";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HomeFolder } from "@/models/HomeLayoutSettings";

interface Props {
  folder: HomeFolder;
  /** How many pages are in it, counting only the ones this reader may open. */
  pageCount: number;
  editing: boolean;
  /** `false` for the last remaining folder, which cannot be deleted. */
  deletable: boolean;
  onOpen: (folderId: string) => void;
  onRename: (folderId: string, name: string) => void;
  onDelete: (folderId: string) => void;
  onMove: (folderId: string, direction: "up" | "down") => void;
  /** A page dropped onto the folder moves into it. */
  onDropPage?: (folderId: string) => void;
}

/**
 * One folder, as a card.
 *
 * While the reader is arranging, the name turns into a field they can type in — renaming a folder
 * is the thing they will do most, and a dialog for it would be three clicks where one will do.
 */
export function FolderTile({
  folder,
  pageCount,
  editing,
  deletable,
  onOpen,
  onRename,
  onDelete,
  onMove,
  onDropPage,
}: Props) {
  const { t } = useTranslation();
  const [draftName, setDraftName] = useState<string | null>(null);

  const commitRename = () => {
    if (draftName !== null) {
      onRename(folder.id, draftName);
      setDraftName(null);
    }
  };

  return (
    <Card
      onDragOver={onDropPage ? event => event.preventDefault() : undefined}
      onDrop={onDropPage ? () => onDropPage(folder.id) : undefined}
      sx={{ borderRadius: 3, height: "100%" }}
    >
      {draftName !== null ? (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ p: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={draftName}
            onChange={event => setDraftName(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") commitRename();
              if (event.key === "Escape") setDraftName(null);
            }}
            label={t("home.folderName", "Ordnername")}
          />
          <IconButton
            size="small"
            onClick={commitRename}
            aria-label={t("home.saveName", "Namen übernehmen")}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center">
          <CardActionArea onClick={() => onOpen(folder.id)} sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <FolderIcon color="primary" />
              <Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {folder.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("home.pageCount", { count: pageCount })}
                </Typography>
              </Stack>
            </Stack>
          </CardActionArea>

          {editing ? (
            <Stack direction="row" sx={{ pr: 0.5 }}>
              <Tooltip title={t("home.rename", "Umbenennen")}>
                <IconButton
                  size="small"
                  onClick={() => setDraftName(folder.name)}
                  aria-label={t("home.rename", "Umbenennen")}
                >
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => onMove(folder.id, "up")}
                aria-label={t("home.moveUp", "Nach vorne")}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onMove(folder.id, "down")}
                aria-label={t("home.moveDown", "Nach hinten")}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
              <Tooltip
                title={
                  deletable
                    ? t("home.deleteFolder", "Ordner löschen")
                    : t("home.lastFolder", "Der letzte Ordner bleibt")
                }
              >
                <IconButton
                  size="small"
                  color="error"
                  disabled={!deletable}
                  onClick={() => onDelete(folder.id)}
                  aria-label={t("home.deleteFolder", "Ordner löschen")}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}
        </Stack>
      )}
    </Card>
  );
}
